// Session 32: the blog review experience, in one place.
//
// Two pages show a blog for review: BlogReview.jsx (a client's post_draft) and
// AdminAdhoc.jsx (an ad hoc blog, agency only). Leena reviews Landry's ad hoc
// drafts because it is the only place she can walk the client's flow herself,
// and it was missing all of this. A copy of it would drift from the real page
// within a session or two, so both pages render the same components and there
// is one version of the truth.
//
// The through-line of the design: a client lands on the finished post, reads
// it, and the obvious next move is "approve" or "tell us what to change".
// Editing the words is a door that is clearly open and deliberately not the
// one you walk into.
import { useEffect, useState } from 'react';
import { supabase } from '../supabase.js';
import { renderMd } from '../lib/markdown.js';
import { SaveStatus, useSaveStatus } from './SaveStatus.jsx';
import { headingsOf } from '../lib/headings.js';

// The heading-anchor rule lives in src/lib/headings.js, plain JS so that
// scripts/slugcheck.mjs can import the rule that actually ships instead of
// checking a copy of it.
export { slugOfHeading, headingsOf } from '../lib/headings.js';

/* ------------------------------------------------------------------ callout */

// Leena, Session 32: "I think it's going to be important at the very top to
// explain this process, so we need a big callout, maybe highlighted in our
// teal colour, that says how to approve / edit or request overall changes."
// Her three routes, in her order, with the one she wants people to take
// carrying the weight at the end.
export function ReviewCallout({ canEdit = true }) {
  return (
    <aside className="review-callout" aria-label="How to review this blog">
      <h2 className="review-callout-head">Below is a preview of your blog</h2>
      <p>You have three ways to go from here.</p>
      <ul className="review-callout-list">
        {/* Photos before approval, not after. Once a post is approved the
            upload is refused, so the other order would walk people into a
            wall. */}
        <li><strong>Happy with it?</strong> Add your photos in the photo section
          below, then approve it.</li>
        <li><strong>Want an overall change?</strong> Ask for a high-level rewrite at
          the bottom of this page and we will write it again.</li>
        {canEdit && (
          <li><strong>Only a few words to fix?</strong> There is an edit-the-text
            option at the bottom as well.</li>
        )}
      </ul>
      <p className="review-callout-ask">
        We would much rather hear the style changes and the big-picture notes than
        have you fix sentences for us. Tell us once and every blog after this one
        comes back closer to what you wanted.
      </p>
    </aside>
  );
}

/* ----------------------------------------------------------- post + editor */

// This component owns the preview as well as the editor, because Session 30
// left the article rendered twice on one page: once as a markdown preview and
// once as the real preview package further down. One of them had to go, and
// the right one to keep is whichever is actually current.
// Session 66: `previewFresh` says the styled preview already shows the
// client's edit (the page works it out from the two timestamps). Until it
// does, their words are shown plainly with a banner, and the page watches
// for the rebuilt preview. Before this the styled preview, pictures and all,
// disappeared for good the moment anyone edited a word.
export function PostAndEditor({ draft, canEdit, previewHtml, previewRef, slug,
                                onSaved, onRefresh, refreshing, previewFresh = true }) {
  const original = draft.body_md || '';
  const edited = draft.body_md_edited || '';
  const current = edited || original;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(current);
  const [excerpt, setExcerpt] = useState(draft.excerpt || '');
  const [resetting, setResetting] = useState(false);
  const save = useSaveStatus();

  useEffect(() => { setText(draft.body_md_edited || draft.body_md || ''); },
            [draft.body_md, draft.body_md_edited]);
  useEffect(() => { setExcerpt(draft.excerpt || ''); }, [draft.excerpt]);

  const h1 = (text.match(/^#\s+(.+)$/m) || [])[1] || '';
  const titleLong = h1.length > 60;

  async function onSave() {
    save.start();
    const { error } = await supabase.rpc('portal_edit_draft_text', {
      p_job_id: draft.job_id,
      p_body_md: text,
      p_excerpt: excerpt.trim() === (draft.excerpt || '').trim() ? null : excerpt,
    });
    if (error) return save.fail(error.message);
    save.ok();
    setEditing(false);
    onSaved?.();
  }

  async function onReset() {
    setResetting(true);
    const { error } = await supabase.rpc('portal_reset_draft_text', { p_job_id: draft.job_id });
    setResetting(false);
    if (error) return save.fail(error.message);
    setEditing(false);
    onSaved?.();
  }

  /* ------------------------------------------------------- reading it ---- */
  if (!editing) {
    return (
      <>
        {onRefresh && (
          <div className="preview-toolbar">
            <button type="button" className={`btn ${refreshing ? 'btn-busy' : ''}`}
                    onClick={onRefresh} disabled={refreshing} aria-pressed={refreshing}>
              {refreshing && <span className="spinner spinner-sm" aria-hidden="true" />}
              {refreshing ? 'Updating…' : 'Refresh preview'}
            </button>
          </div>
        )}

        {edited && !previewFresh ? (
          // Their edit is being laid out on the droplet (a minute or two).
          // Until the styled preview carries it, show their words plainly
          // rather than our old version.
          <>
            <p className="banner banner-info">
              Laying out your changes. The styled preview, with the pictures, updates in a minute or two.
            </p>
            <article className="md-doc reader-md" dangerouslySetInnerHTML={{ __html: renderMd(current) }} />
          </>
        ) : previewHtml ? (
          <iframe
            ref={previewRef}
            className="reader-frame"
            title={`Preview of ${draft.title || slug}`}
            srcDoc={previewHtml}
            sandbox=""
            referrerPolicy="no-referrer"
          />
        ) : original ? (
          <article className="md-doc reader-md" dangerouslySetInnerHTML={{ __html: renderMd(original) }} />
        ) : (
          <div className="empty">
            <h2>Preview isn't ready yet</h2>
            <p>
              The draft was sent but the preview package hasn't uploaded.
              {onRefresh ? ' Use Refresh preview above in a minute, or the emailed body for review.'
                         : ' It builds a minute or two after the draft; reload the page shortly.'}
            </p>
          </div>
        )}

        {canEdit && original && (
          <div className="next-step">
            <p>
              <strong>Read it through.</strong> If something needs changing, the quickest
              route is to tell us below and we will rewrite it. Most people never need
              to do more than that.
            </p>
            <div className="next-step-actions">
              <button type="button" className="btn" onClick={() => { setText(current); setEditing(true); }}>
                Or edit the words yourself
              </button>
              {edited && (
                <button type="button" className="link-btn" disabled={resetting} onClick={onReset}>
                  {resetting ? 'Putting it back…' : 'Put it back the way we wrote it'}
                </button>
              )}
              <SaveStatus status={save.status} />
            </div>
          </div>
        )}
      </>
    );
  }

  /* ------------------------------------------------------- editing it ---- */
  return (
    <section className="review-section blog-edit">
      <div className="blog-edit-head"><h2>Editing the words</h2></div>
      <p className="hint">
        Markdown: <code>#</code> is the title, <code>##</code> a heading, <code>**bold**</code>,
        and a blank line starts a new paragraph. Everything else is just writing.
      </p>
      <p className="hint">
        Renaming a heading moves the photo that sits under it. We put each one back
        under the nearest section, but check the photo list after you save.
      </p>
      {titleLong && (
        <p className="banner banner-info">
          Your title is {h1.length} characters. Google usually cuts titles off around 60,
          so some of it may not show in search results. Shorten it if you can, and keep it
          if you cannot. <strong>You can save either way.</strong>
        </p>
      )}

      <label className="field">
        <span className="field-label">The summary</span>
        <textarea
          id={`excerpt-${draft.job_id}`}
          className="review-textarea"
          rows={2}
          value={excerpt}
          maxLength={2000}
          onChange={(e) => setExcerpt(e.target.value)}
          placeholder="One or two sentences describing the post."
        />
      </label>
      <p className="hint">
        This is what shows under your post in Google results and on your blog index.
        It does not change when you edit the article, so if you have altered what the
        post is actually about, change it here too.
      </p>

      <label className="field"><span className="field-label">The article</span></label>
      <textarea
        id={`body-${draft.job_id}`}
        className="review-textarea blog-edit-area"
        rows={26}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
        <button type="button" className="btn btn-primary" onClick={onSave}
                disabled={save.status === 'saving' || !text.trim()}>
          {save.status === 'saving' ? 'Saving…' : 'Save my changes'}
        </button>
        <button type="button" className="link-btn" onClick={() => { setText(current); setEditing(false); }}>
          Cancel
        </button>
        <SaveStatus status={save.status} />
      </div>
    </section>
  );
}

/* --------------------------------------------------------------- the photos */

// Leena, Session 32: "I think we need to tell them that all the photos should
// be after headlines. When we're asking them to upload the photo, we can tell
// them which headline it's going to be under. If they want to change the
// location, maybe we're just giving them the option to move it under different
// headlines."
//
// So a photo is named by the headline it sits under, never by its slot id —
// "body-2" means nothing to anyone — and Move to sits on the same row as
// Upload, because the moment you want to move a photo is the moment you are
// looking at it.
export function ImageSlotsSection({ draft, canEdit, fills, uploading, skipping,
                                    onUploadSlot, onToggleSkip, onChanged, needsHero }) {
  const slots = draft.image_slots || [];
  const heads = headingsOf(draft.body_md_edited || draft.body_md);
  const headingFor = Object.fromEntries(heads.map((h) => ['after-' + h.anchor, h.heading]));
  const taken = new Set(slots.filter((s) => s.slot_id !== 'hero').map((s) => s.position));
  const orphans = slots.filter((s) => s.slot_id !== 'hero' && !headingFor[s.position]);

  return (
    <section className="review-section">
      <h2>Photos for this post</h2>
      {slots.length === 0 ? (
        <p className="hint">
          This draft doesn't have photo spots yet. We'll pick up anything you send
          us by email the usual way.
        </p>
      ) : (
        <>
          <p className="hint">
            Every photo sits under one of the headlines in your post. Each row below
            says which headline its photo goes under. Upload there, and if you would
            rather it went somewhere else, use <em>Move to</em> to pick a different
            headline — you can change that at any time before the post goes out.
          </p>
          <p className="hint">
            Do add a short description of each photo. It is what Google reads to
            understand the picture and what a screen reader says out loud, and you
            are the only one who knows what is actually in the shot.
          </p>

          {orphans.length > 0 && (
            <p className="banner banner-gate">
              <strong>
                {orphans.length === 1 ? 'One photo spot does' : `${orphans.length} photo spots do`}
                {' '}not match a headline in the post any more.
              </strong>{' '}
              A photo there would not appear at all. Use <em>Move to</em> on
              {orphans.length === 1 ? ' that row' : ' those rows'} to put it under a
              real headline, or leave it empty.
            </p>
          )}

          {canEdit && <AddPhotoRow draft={draft} heads={heads} taken={taken} onChanged={onChanged} />}

          {slots.map((slot) => {
            const f = fills[slot.slot_id];
            const filled = !!(f?.storage_path || f?.image_path);
            const skipped = !!f?.skipped;
            const busyUp = uploading[slot.slot_id];
            const busySkip = skipping[slot.slot_id];
            const isHero = slot.slot_id === 'hero';
            const canUpload = canEdit || (isHero && needsHero);
            const mine = !!slot.added_by_client;
            const heading = headingFor[slot.position];
            const orphan = !isHero && !heading;
            // The current position stays selectable so the dropdown can show
            // where the photo is now; every other taken headline is hidden.
            const options = heads.filter((h) => !taken.has('after-' + h.anchor)
                                                || 'after-' + h.anchor === slot.position);
            return (
              <div key={slot.slot_id}
                   className={`slot-row${skipped ? ' slot-row-skipped' : ''}${orphan ? ' slot-row-orphan' : ''}`}>
                <div className="slot-meta">
                  <div className="slot-name">
                    {isHero ? 'Main photo, at the top' : (heading || 'No matching headline')}
                    {isHero && <span className="required-pill">required</span>}
                    {mine && <span className="chip course-chip">yours</span>}
                  </div>
                  <div className="slot-purpose">
                    {isHero ? slot.purpose
                            : orphan ? `This spot was set for a headline the post no longer has.`
                                     : `Goes under the headline "${heading}".`}
                  </div>
                </div>
                <div className="slot-status">
                  {filled ? (
                    <span className="slot-filled">Uploaded ✓ {f?.filename ? `(${f.filename})` : ''}</span>
                  ) : skipped ? (
                    <span className="slot-skipped">Skipped — no photo here</span>
                  ) : (
                    <span className="slot-empty">Awaiting your upload</span>
                  )}
                </div>
                <div className="slot-cta">
                  {!skipped && (
                    <label className="btn" aria-disabled={!canUpload || busyUp}>
                      {busyUp ? 'Uploading…' : (filled ? 'Replace' : 'Upload')}
                      <input
                        type="file"
                        accept="image/*"
                        disabled={busyUp || !canUpload}
                        style={{ display: 'none' }}
                        onChange={(e) => onUploadSlot(slot, e.target.files?.[0])}
                      />
                    </label>
                  )}
                </div>
                <div className="slot-cta slot-cta-move">
                  {!isHero && canEdit && heads.length > 0 && (
                    <MoveSelect draft={draft} slot={slot} options={options}
                                hasPosition={!!heading} onChanged={onChanged} />
                  )}
                </div>
                {canEdit && (
                  <div className="slot-describe">
                    <DescribeInput draft={draft} slot={slot} fill={f} onChanged={onChanged} />
                  </div>
                )}
                <div className="slot-cta">
                  {!isHero && canEdit && (
                    // A spot they added is theirs to take away, photo or not.
                    // Ours are skipped rather than removed, so the reason we
                    // wanted a photo there is not lost.
                    mine ? (
                      <RemoveButton draft={draft} slotId={slot.slot_id} onChanged={onChanged} />
                    ) : skipped ? (
                      <button type="button" className="link-btn" disabled={busySkip}
                              onClick={() => onToggleSkip(slot.slot_id, false)}>
                        {busySkip ? 'Updating…' : 'Add a photo after all'}
                      </button>
                    ) : !filled ? (
                      <button type="button" className="link-btn" disabled={busySkip}
                              onClick={() => onToggleSkip(slot.slot_id, true)}>
                        {busySkip ? 'Updating…' : 'No photo here'}
                      </button>
                    ) : null
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}
    </section>
  );
}

// One RPC, one spinner, one error line. Used by the three slot controls below.
function useSlotRpc(onChanged) {
  const [busy, setBusy] = useState(false);
  const save = useSaveStatus();
  async function call(fn, args) {
    setBusy(true); save.start();
    const { error } = await supabase.rpc(fn, args);
    setBusy(false);
    if (error) return save.fail(error.message);
    save.ok();
    onChanged?.();
  }
  return { busy, save, call };
}

function AddPhotoRow({ draft, heads, taken, onChanged }) {
  const [at, setAt] = useState('');
  const { busy, save, call } = useSlotRpc(onChanged);
  const free = heads.filter((h) => !taken.has('after-' + h.anchor));
  if (!heads.length) return null;
  return (
    <div className="slot-add">
      <label className="field-label" htmlFor={`addslot-${draft.job_id}`}>Add a photo under</label>
      <select id={`addslot-${draft.job_id}`} className="course-input" value={at}
              disabled={busy || !free.length}
              onChange={(e) => setAt(e.target.value)}>
        <option value="">{free.length ? 'Choose a headline…' : 'Every headline already has one'}</option>
        {free.map((h) => <option key={h.anchor} value={'after-' + h.anchor}>{h.heading}</option>)}
      </select>
      <button type="button" className="btn" disabled={busy || !at}
              onClick={async () => {
                await call('portal_add_image_slot', { p_job_id: draft.job_id, p_position: at });
                setAt('');
              }}>
        Add
      </button>
      <SaveStatus status={save.status} />
    </div>
  );
}

// Saved when they leave the box rather than on every keystroke: one row per
// photo and six photos is a lot of writes otherwise, and a description is a
// sentence somebody finishes before moving on.
function DescribeInput({ draft, slot, fill, onChanged }) {
  const saved = fill?.client_alt || '';
  const [text, setText] = useState(saved);
  const { busy, save, call } = useSlotRpc(onChanged);

  useEffect(() => { setText(fill?.client_alt || ''); }, [fill?.client_alt]);

  async function commit() {
    if (text.trim() === saved.trim()) return;
    await call('portal_set_image_alt', {
      p_job_id: draft.job_id, p_slot_id: slot.slot_id, p_alt: text,
    });
  }

  return (
    <label className="slot-describe-field">
      <span className="field-label">What is in this photo?</span>
      <input
        type="text"
        value={text}
        maxLength={300}
        disabled={busy}
        placeholder={slot.slot_id === 'hero'
          ? 'e.g. A family of four on the beach at sunset, Ventura pier behind them'
          : 'A short description of this picture'}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      />
      <SaveStatus status={save.status} />
    </label>
  );
}

function MoveSelect({ draft, slot, options, hasPosition, onChanged }) {
  const { busy, save, call } = useSlotRpc(onChanged);
  return (
    <>
      <select
        className="course-input slot-move-select"
        aria-label={`Move this photo to a different headline`}
        value={hasPosition ? slot.position : ''}
        disabled={busy}
        onChange={(e) => e.target.value && call('portal_move_image_slot', {
          p_job_id: draft.job_id, p_slot_id: slot.slot_id, p_position: e.target.value,
        })}
      >
        <option value="">Move to…</option>
        {options.map((h) => (
          <option key={h.anchor} value={'after-' + h.anchor}>{h.heading}</option>
        ))}
      </select>
      <SaveStatus status={save.status} />
    </>
  );
}

function RemoveButton({ draft, slotId, onChanged }) {
  const { busy, save, call } = useSlotRpc(onChanged);
  return (
    <>
      <button type="button" className="link-btn" disabled={busy}
              onClick={() => call('portal_remove_image_slot', { p_job_id: draft.job_id, p_slot_id: slotId })}>
        Remove this spot
      </button>
      <SaveStatus status={save.status} />
    </>
  );
}

/* ------------------------------------------------------------- the uploads */

// The upload and skip handlers, lifted out of BlogReview so the ad hoc page
// gets the identical behaviour rather than a second implementation of it.
// `onFill` is how a page says "a photo just landed" — BlogReview uses it to
// start watching for the rebuilt preview. It gets 'skip' or 'unskip' when the
// change was "No photo here" / "Add a photo after all" rather than an upload.
export function useSlotUploads({ jobId, clientId, slug, reload, onBanner, onFill }) {
  const [uploading, setUploading] = useState({});
  const [skipping, setSkipping] = useState({});

  async function onUploadSlot(slot, file) {
    if (!file) return;
    setUploading((u) => ({ ...u, [slot.slot_id]: true }));
    try {
      const safeName = file.name.replace(/[^\w.\-]+/g, '_');
      const path = `${clientId}/${slug}/${slot.slot_id}/${safeName}`;
      const { error: upErr } = await supabase.storage
        .from('post-images').upload(path, file, { upsert: true, contentType: file.type || 'image/jpeg' });
      if (upErr) throw upErr;
      const { error: rpcErr } = await supabase.rpc('portal_attach_image', {
        p_job_id: jobId, p_slot_id: slot.slot_id, p_storage_path: path,
      });
      if (rpcErr) throw rpcErr;
      onBanner?.({ tone: 'ok', text: 'Photo uploaded.' });
      await reload?.();
      onFill?.();
    } catch (e) {
      onBanner?.({ tone: 'err', text: `Upload failed: ${e.message || e}` });
    } finally {
      setUploading((u) => ({ ...u, [slot.slot_id]: false }));
    }
  }

  async function onToggleSkip(slotId, skip) {
    setSkipping((s) => ({ ...s, [slotId]: true }));
    try {
      const { error } = await supabase.rpc('portal_skip_image_slot', {
        p_job_id: jobId, p_slot_id: slotId, p_skip: skip,
      });
      if (error) throw error;
      onBanner?.({ tone: 'ok', text: skip ? 'Left that spot empty.' : 'That spot is back.' });
      await reload?.();
      // Session 60: the page words its "updating" line by what just happened.
      onFill?.(skip ? 'skip' : 'unskip');
    } catch (e) {
      onBanner?.({ tone: 'err', text: `Couldn't update that spot: ${e.message || e}` });
    } finally {
      setSkipping((s) => ({ ...s, [slotId]: false }));
    }
  }

  return { uploading, skipping, onUploadSlot, onToggleSkip };
}
